import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {NearbyLocationType} from 'src/components/elements/home/NearbyLocationView';
import {RecentLocationType} from 'src/components/elements/home/RecentLocationView';
import {AppDispatch, RootState} from 'src/redux/store';
import Events from 'src/logging/Events';
import AppApi from 'src/api/AppApi';
import PlatformApi from 'src/api/PlatformApi';
import AccountApi from 'src/api/AccountApi';
import {AnnouncementType} from 'src/types/AnnouncementType';
import {generateErrorMessage} from '../../logging/generateErrorMessage';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import {Location} from '../../types/serverTypes/Location';
import ActionsFactory from 'src/actions/ActionsFactory';

interface ICampusLocationState {
  selectedLocation?: Location;
  recentCampusesAndLocations: Array<RecentLocationType>;
  nearbyCampusesAndLocations: Array<NearbyLocationType>;
  diningLocations: Array<Location>;
  announcements: Array<AnnouncementType>;
  loading: boolean;
  locallyDismissedAnnouncements: string[];
}
type ThunkConfig = {
  state: RootState;
  dispatch: AppDispatch;
  extra?: unknown;
  rejectValue?: string;
};

export const initialState: ICampusLocationState = {
  selectedLocation: null,
  recentCampusesAndLocations: [],
  nearbyCampusesAndLocations: [],
  diningLocations: [],
  announcements: [],
  loading: false,
  locallyDismissedAnnouncements: [],
};

export const fetchRecentCampusesAndLocations = createAsyncThunk<
  {
    recentLocations: RecentLocationType[];
    selectedLocation: Location | null;
  },
  string,
  ThunkConfig
>(
  'campusLocation/fetchRecentCampusesAndLocations',
  async (accountId, {getState}) => {
    let recentLocationsAndCampusesData;
    try {
      recentLocationsAndCampusesData =
        await AppApi.fetchRecentLocationsAndCampuses(accountId);
      let recentLocationsData: RecentLocationType[] =
        recentLocationsAndCampusesData?.filter(
          (item: RecentLocationType) => item?.locationId !== null,
        );

      // If no recent locations, try fetching linked locations
      if (recentLocationsData.length === 0) {
        const linkedLocationsData = await AccountApi.getLinkedLocations(
          accountId,
        );
        if (linkedLocationsData && linkedLocationsData.length > 0) {
          // Convert linked locations to recent location format
          recentLocationsData = linkedLocationsData.map((loc: any) => ({
            locationId: loc.locationId || loc.id,
            name: loc.name,
            displayName: loc.name,
            city: loc.city || '',
            state: loc.state || '',
            campusId: null,
            lastUpdated: new Date().toISOString(),
          }));
        }
      }

      if (
        recentLocationsData.length > 0 &&
        recentLocationsData[0].locationId !== null
      ) {
        const selectedLocation: RecentLocationType = recentLocationsData[0];
        const location = await PlatformApi.fetchLocation(
          selectedLocation.locationId,
        );
        return {
          recentLocations: recentLocationsData,
          selectedLocation: location,
        };
      }

      // Check Home Location
      const accountLocationId = getState().account.account.locationId;
      if (accountLocationId) {
        const location = await PlatformApi.fetchLocation(accountLocationId);
        return {
          //TODO:SM: Field mismatch between types. Refactoring needed.
          recentLocations: [location as undefined as RecentLocationType],
          selectedLocation: location,
        };
      }

      return {recentLocations: [], selectedLocation: null};
    } catch (error) {
      CrashlyticsEvents.log(
        'Exception',
        'campusLocationSlice:fetchRecentCampusesAndLocations',
        generateErrorMessage(error),
        recentLocationsAndCampusesData,
      );
      Events.Error.trackEvent(
        'Exception',
        'campusLocationSlice:fetchRecentCampusesAndLocations',
        generateErrorMessage(error),
      );
      return {recentLocations: [], selectedLocation: null};
    }
  },
);

export const fetchNearbyCampusesAndLocations = createAsyncThunk(
  'campusLocation/fetchNearbyCampusesAndLocations',
  async (position: {lat: number; lon: number}) => {
    let nearbyLocations;
    try {
      nearbyLocations = await AppApi.fetchNearbyLocationsAndCampuses(
        position.lat,
        position.lon,
      );
      return nearbyLocations;
    } catch (error) {
      CrashlyticsEvents.log(
        'Exception',
        'campusLocationSlice:fetchNearbyCampusesAndLocations',
        generateErrorMessage(error),
        nearbyLocations,
      );
      Events.Error.trackEvent(
        'Exception',
        'campusLocationSlice:fetchNearbyCampusesAndLocations',
        generateErrorMessage(error),
      );
      throw error;
    }
  },
);

export const fetchCampusLocations = createAsyncThunk(
  'campusLocation/fetchCampusLocations',
  async (locationId: string) => {
    try {
      let diningLocation = [];
      const location = await PlatformApi.fetchLocation(locationId);
      if (location?.onlineOrderConfig) {
        diningLocation = [
          {
            localType: 'market',
            ...location,
            datecreated: location.dateCreated,
            id: location.locationId,
            deviceId: location.locationId,
            org: location.orgId,
            locationType: 'sos',
            orderAhead: true,
          },
        ];
      }
      return diningLocation;
    } catch (error) {
      CrashlyticsEvents.log(
        'Exception',
        'campusLocationSlice:fetchCampusLocations',
        generateErrorMessage(error),
      );
      Events.Error.trackEvent(
        'Exception',
        'campusLocationSlice:fetchCampusLocations',
        generateErrorMessage(error),
      );
      throw error;
    }
  },
);

export const swicthCurrentCampusesAndLocations = createAsyncThunk<
  void,
  {accountId: string; locationId: string},
  ThunkConfig
>(
  'campusLocation/swicthCurrentCampusesAndLocations',
  async (data: {accountId: string; locationId: string}, {dispatch}) => {
    try {
      await AppApi.switchCurrentLocationOrCampus(
        data.accountId,
        data.locationId,
      );
      await dispatch(fetchRecentCampusesAndLocations(data.accountId));
      const actions = ActionsFactory.getAccountActions();
      await actions.switchCurrentLocation();
    } catch (error) {
      CrashlyticsEvents.log(
        'Exception',
        'campusLocationSlice:swicthCurrentCampusesAndLocations',
        generateErrorMessage(error),
      );
      Events.Error.trackEvent(
        'Exception',
        'campusLocationSlice:swicthCurrentCampusesAndLocations',
        generateErrorMessage(error),
      );
      throw error;
    }
  },
);

export const fetchAnnouncements = createAsyncThunk<
  AnnouncementType[],
  {locationId: string; accountId: string},
  ThunkConfig
>('campusLocation/fetchAnnouncements', async ({locationId, accountId}) => {
  try {
    const announcementsList: AnnouncementType[] =
      await AppApi.fetchAnnouncements(locationId, accountId);
    if (announcementsList?.length > 0 && announcementsList[0]?.announcementId) {
      return announcementsList;
    } else {
      return [];
    }
  } catch (error) {
    CrashlyticsEvents.log(
      'Exception',
      'campusLocationSlice:fetchAnnouncements',
      generateErrorMessage(error),
    );
    Events.Error.trackEvent(
      'Exception',
      'campusLocationSlice:fetchAnnouncements',
      generateErrorMessage(error),
    );
    throw error;
  }
});

export const dismissAnnouncement = createAsyncThunk<
  void,
  {announcementId: string},
  ThunkConfig
>(
  'campusLocation/dismissAnnouncement',
  async ({announcementId}, {getState}) => {
    try {
      const accountId = getState().account.account.id;
      await AppApi.dismissAnnouncement(accountId, announcementId);
    } catch (error) {
      CrashlyticsEvents.log(
        'Exception',
        'campusLocationSlice:dismissAnnouncement',
        generateErrorMessage(error),
      );
      Events.Error.trackEvent(
        'Exception',
        'campusLocationSlice:dismissAnnouncement',
        generateErrorMessage(error),
      );
      throw error;
    }
  },
);

export const deleteRecentCampusLocation = createAsyncThunk(
  'campusLocation/deleteRecentCampusLocation',
  async (data: {accountId: string; locationId: string}) => {
    try {
      const locations = await AppApi.fetchRecentLocationsAndCampuses(
        data.accountId,
      );
      const locationToDelete = locations.find(
        (loc) =>
          loc.locationId === data.locationId ||
          loc.campusId === data.locationId,
      );
      if (!locationToDelete) {
        throw new Error(
          `No recent location found with locationId ${data.locationId}`,
        );
      }
      const recentLocationsId = locationToDelete.recentLocationsId;
      const response = await AppApi.deleteRecentLocationsAndCampuses(
        data.accountId,
        recentLocationsId,
      );
      if (response.status !== 'ok') {
        throw new Error(`Request failed with status ${response.status}`);
      }
      return data;
    } catch (error) {
      CrashlyticsEvents.log(
        'Exception',
        'campusLocationSlice:deleteRecentCampusLocation',
        generateErrorMessage(error),
      );
      Events.Error.trackEvent(
        'Exception',
        'campusLocationSlice:deleteRecentCampusLocation',
        generateErrorMessage(error),
      );
      throw error;
    }
  },
);

const campusLocationSlice = createSlice({
  name: 'campusLocation',
  initialState,
  reducers: {
    locallyDismissAnnouncement: (
      state,
      action: PayloadAction<{announcementId: string}>,
    ) => {
      const {announcementId} = action.payload;
      if (!state.locallyDismissedAnnouncements.includes(announcementId)) {
        state.locallyDismissedAnnouncements.push(announcementId);
      }
    },
    addLinkedLocationToRecents: (
      state,
      action: PayloadAction<RecentLocationType>,
    ) => {
      const newLocation = action.payload;
      // Check if location already exists
      const exists = state.recentCampusesAndLocations.some(
        (loc) => loc.locationId === newLocation.locationId,
      );
      if (!exists) {
        // Add to beginning of the list
        state.recentCampusesAndLocations.unshift(newLocation);
        // Keep only top 5
        state.recentCampusesAndLocations =
          state.recentCampusesAndLocations.slice(0, 5);
      }
    },
    setSelectedLocation: (state, action: PayloadAction<Location>) => {
      state.selectedLocation = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchRecentCampusesAndLocations.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchRecentCampusesAndLocations.rejected, (state) => {
      state.recentCampusesAndLocations = [];
      state.selectedLocation = null;
      state.loading = false;
    });
    builder.addCase(
      fetchRecentCampusesAndLocations.fulfilled,
      (state, action) => {
        // Merge with existing locations to preserve manually added ones
        const existingLocations = state.recentCampusesAndLocations;
        const newLocations = action.payload.recentLocations;

        // Create a map of existing location IDs
        const existingIds = new Set(
          existingLocations.map((loc) => loc.locationId),
        );

        // Add new locations that don't exist yet
        const mergedLocations = [...existingLocations];
        newLocations.forEach((newLoc) => {
          if (!existingIds.has(newLoc.locationId)) {
            mergedLocations.push(newLoc);
          }
        });

        state.recentCampusesAndLocations = mergedLocations.slice(0, 5);

        // Only update selectedLocation if we don't have one yet, or if the new one is different
        if (!state.selectedLocation || action.payload.selectedLocation) {
          state.selectedLocation =
            action.payload.selectedLocation || state.selectedLocation;
        }

        state.loading = false;
      },
    );
    builder.addCase(fetchNearbyCampusesAndLocations.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchNearbyCampusesAndLocations.rejected, (state) => {
      state.nearbyCampusesAndLocations = [];
      state.loading = false;
    });
    builder.addCase(
      fetchNearbyCampusesAndLocations.fulfilled,
      (state, action) => {
        if (action.payload?.items) {
          state.nearbyCampusesAndLocations = action.payload.items;
        }
        state.loading = false;
      },
    );
    builder.addCase(swicthCurrentCampusesAndLocations.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(swicthCurrentCampusesAndLocations.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(swicthCurrentCampusesAndLocations.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(fetchCampusLocations.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchCampusLocations.rejected, (state) => {
      state.diningLocations = [];
      state.loading = false;
    });
    builder.addCase(fetchCampusLocations.fulfilled, (state, action) => {
      state.diningLocations = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchAnnouncements.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchAnnouncements.rejected, (state) => {
      state.announcements = [];
      state.loading = false;
    });
    builder.addCase(fetchAnnouncements.fulfilled, (state, action) => {
      state.announcements = action.payload;
      state.loading = false;
    });
    builder.addCase(deleteRecentCampusLocation.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteRecentCampusLocation.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(
      deleteRecentCampusLocation.fulfilled,
      (state, {payload}) => {
        state.loading = false;
        state.recentCampusesAndLocations =
          state.recentCampusesAndLocations.filter(
            (item: RecentLocationType) =>
              (item.campusId ?? item.locationId) !== payload.locationId,
          );
        if (
          state.selectedLocation &&
          (state.selectedLocation.campusId ??
            state.selectedLocation.locationId) === payload.locationId
        ) {
          state.selectedLocation = null;
        }
      },
    );
  },
});
export const {
  locallyDismissAnnouncement,
  addLinkedLocationToRecents,
  setSelectedLocation,
} = campusLocationSlice.actions;

export const selectLocallyDismissedAnnouncements = (state: RootState) =>
  state.campusLocation.locallyDismissedAnnouncements;

export default campusLocationSlice.reducer;
